import {
  AcceptInviteAsNewUserResultSchema,
  AcceptInviteResultSchema,
} from '@playerbase/types';

import { supabase } from '../lib/supabase.js';

const INVITE_TOKEN_STORAGE_KEY = 'playerbase.invite.token';

export function readStashedInviteToken(): string | null {
  try {
    return localStorage.getItem(INVITE_TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function clearStashedInviteToken(): void {
  try {
    localStorage.removeItem(INVITE_TOKEN_STORAGE_KEY);
  } catch {
    // localStorage may be unavailable
  }
}

export function stashInviteToken(token: string): void {
  try {
    localStorage.setItem(INVITE_TOKEN_STORAGE_KEY, token);
  } catch {
    // localStorage may be unavailable
  }
}

// Tries the device-local stash first, then falls back to the auth user's
// metadata (which the email-signup path writes as `data.invite_token` so the
// token survives a different browser context).
async function getPendingInviteToken(): Promise<{ token: string; fromMetadata: boolean } | null> {
  const stashed = readStashedInviteToken();
  if (stashed !== null) return { token: stashed, fromMetadata: false };
  const { data, error } = await supabase.auth.getUser();
  if (error !== null || data.user === null) return null;
  const meta = data.user.user_metadata ?? {};
  const fromMeta = typeof meta.invite_token === 'string' ? meta.invite_token : null;
  if (fromMeta === null) return null;
  return { token: fromMeta, fromMetadata: true };
}

async function clearMetadataInviteToken(): Promise<void> {
  // Best-effort - if it fails, the next redemption attempt will see the token
  // in metadata, the RPC will return invite_already_accepted, and the warn
  // log will note it. Not worth blocking the user on.
  try {
    await supabase.auth.updateUser({ data: { invite_token: null } });
  } catch (err) {
    console.warn('[invite-redemption] failed to clear metadata token:', err);
  }
}

export type InviteRedemption = { id: string; shortId: string };

// The invite RPCs return the studio's id/name/slug but not its short_id (the
// opaque URL key). A quick follow-up read resolves it; by this point the
// caller is a member, so RLS allows the select.
async function fetchStudioShortId(studioId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('studios')
    .select('short_id')
    .eq('id', studioId)
    .single();
  if (error !== null || data === null || typeof data.short_id !== 'string') {
    return null;
  }
  return data.short_id;
}

// Redeem a stashed invite token for the currently-signed-in user. Returns the
// inviting studio's id and short_id on success so the caller can pin it as the
// active studio AND navigate there. Returns null when there's nothing to
// redeem, or when the redemption fails - in that case the caller falls back
// to whatever default routing they had.
//
// Memberships drive which RPC we call. 0 memberships -> new-via-invite path
// (auto-creates the personal studio); >0 -> existing-user accept.
//
// Called from both /auth/callback (covers the same-browser email-confirm and
// OAuth flows) and /welcome (catches every path that lands a 0-membership
// user at the studio-setup form, including cross-browser sign-in).
export async function redeemPendingInvite(): Promise<InviteRedemption | null> {
  const found = await getPendingInviteToken();
  if (found === null) return null;
  const { token } = found;
  // Clear up-front so a half-failed redemption doesn't leave a stale token
  // lying around between sessions. Metadata clear is best-effort, fire-and-
  // forget - if it fails, the next redemption attempt sees the token, the
  // RPC returns invite_already_accepted, and the warn log notes it.
  clearStashedInviteToken();
  void clearMetadataInviteToken();

  const { data: membershipsData, error: mErr } = await supabase
    .from('studio_members')
    .select('id')
    .limit(1);
  if (mErr !== null) {
    console.warn('[invite-redemption] membership lookup failed:', mErr.message);
    return null;
  }
  const hasMembership = (membershipsData ?? []).length > 0;

  if (!hasMembership) {
    const { data, error } = await supabase.rpc('accept_studio_invite_as_new_user', { p_token: token });
    if (error !== null) {
      console.warn('[invite-redemption] accept-as-new-user failed:', error.message);
      return null;
    }
    const parsed = AcceptInviteAsNewUserResultSchema.safeParse(data);
    if (!parsed.success) return null;
    const s = parsed.data.invitingStudio;
    const shortId = await fetchStudioShortId(s.id);
    if (shortId === null) return null;
    return { id: s.id, shortId };
  }

  const { data, error } = await supabase.rpc('accept_studio_invite', { p_token: token });
  if (error !== null) {
    console.warn('[invite-redemption] accept failed:', error.message);
    return null;
  }
  const parsed = AcceptInviteResultSchema.safeParse(data);
  if (!parsed.success) return null;
  const s = parsed.data.studio;
  const shortId = await fetchStudioShortId(s.id);
  if (shortId === null) return null;
  return { id: s.id, shortId };
}
